import { useEffect, useState } from "react";
import { Lock, Smartphone, CheckCircle2, Clock, UserPlus, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getGuessPayInfo, guessPayQrUrl, requestUnlock, unlockStatus, memberRegister, memberLogin, memberForgot, apiErrorMessage } from "@/services/api";
import { unlockStore, memberStore } from "@/utils/unlockStore";

const dialogCls = "max-h-[92vh] max-w-sm overflow-y-auto border-[color:var(--border-gold)] bg-[color:var(--bg-card)] text-[color:var(--text-primary)]";
const inputCls = "w-full rounded-lg border border-[color:var(--border-gold)] bg-white px-3 py-2 text-sm text-[#111] outline-none focus:border-[color:var(--accent-crimson)]";

// Polls pending payment requests; calls onUnlocked when any gets approved.
export function useUnlockSync(onUnlocked) {
  useEffect(() => {
    const tick = async () => {
      const ids = unlockStore.pendingIds();
      if (!ids.length || !memberStore.get()) return;
      try {
        const { items } = await unlockStatus(ids);
        let changed = false;
        items.forEach((r) => { if (r.status !== "pending") { unlockStore.apply(r); changed = true; } });
        if (changed) { onUnlocked(); toast.success("Payment approved — guessing unlocked!"); }
      } catch { /* retry next tick */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    const onMember = () => onUnlocked();
    window.addEventListener("bgr-member", onMember);
    return () => { clearInterval(id); window.removeEventListener("bgr-member", onMember); };
  }, [onUnlocked]);
}

export function LockedGuess({ guessing, testid, onUnlocked }) {
  const [open, setOpen] = useState(false);
  const needLogin = guessing.login_required;
  const badge = needLogin ? "Login / Register" : `₹${guessing.price} Unlock`;
  return (
    <>
      <button type="button" data-testid={`guessing-unlock-${testid}`} onClick={() => setOpen(true)}
        className="flex w-full items-center justify-center gap-1.5 py-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[color:var(--accent-crimson)] transition-colors hover:text-[color:var(--accent-gold-bright)]">
        <Lock className="h-3.5 w-3.5" /> Today's Guessing
        <span className="rounded-full bg-[#0B7A3B] px-2 py-[1px] text-[9px] text-white">{badge}</span>
      </button>
      {needLogin
        ? <AuthDialog open={open} onOpenChange={setOpen} onDone={() => { setOpen(false); onUnlocked(); }} />
        : <PayDialog open={open} onOpenChange={setOpen} title={`${guessing.category_name} — Today's Guessing`}
            desc={`Ank, Jodi, Panna, Half & Full Sangam · for today only · ₹${guessing.price}`} price={guessing.price} note={guessing.category_name}
            onRequest={() => requestUnlock({ category_id: guessing.category_id })} onUnlocked={onUnlocked} />}
    </>
  );
}

export function AuthDialog({ open, onOpenChange, onDone }) {
  const [mode, setMode] = useState("register");
  const [f, setF] = useState({ mobile: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const reg = mode === "register";
  const forgot = mode === "forgot";
  const ok = f.mobile.length === 10 && (forgot || (f.password.length >= 6 && (!reg || f.name.trim().length >= 2)));

  const submit = async () => {
    setBusy(true);
    try {
      if (forgot) {
        await memberForgot(f.mobile);
        toast.success("Request sent — admin will share your new password");
        setMode("login");
        return;
      }
      const m = reg ? await memberRegister(f) : await memberLogin({ mobile: f.mobile, password: f.password });
      memberStore.set(m);
      toast.success(`Welcome ${m.name}!`);
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="member-auth-dialog" className={dialogCls}>
        <DialogHeader>
          <DialogTitle className="font-display">{forgot ? "Forgot password?" : reg ? "Register" : "Login"}</DialogTitle>
          <DialogDescription>An account is required to view Today's Guessing</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-[color:var(--bg-secondary)] p-1">
          {[["register", "Register", UserPlus], ["login", "Login", LogIn]].map(([k, l, I]) => (
            <button key={k} type="button" data-testid={`member-tab-${k}`} onClick={() => setMode(k)}
              className={`inline-flex items-center justify-center gap-1 rounded-md py-1.5 text-xs font-bold ${mode === k ? "btn-gold" : "text-[color:var(--text-secondary)]"}`}>
              <I className="h-3.5 w-3.5" /> {l}
            </button>
          ))}
        </div>
        <div className="space-y-2.5">
          <input data-testid="member-mobile" inputMode="numeric" placeholder="Mobile number (10 digit)" className={inputCls} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value.replace(/\D/g, "").slice(-10) })} />
          {reg && <input data-testid="member-name" placeholder="Your name" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
          {!forgot && <input data-testid="member-password" type="password" placeholder="Password (min 6 characters)" className={inputCls} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />}
          <button type="button" data-testid="member-submit" onClick={submit} disabled={busy || !ok}
            className="btn-gold flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-bold disabled:opacity-50">
            {busy ? "..." : forgot ? "Send reset request" : reg ? "Register" : "Login"}
          </button>
          {mode === "login" && <button type="button" data-testid="member-forgot-link" onClick={() => setMode("forgot")} className="w-full text-center text-xs font-semibold text-[color:var(--accent-crimson)] underline">Password bhool gaye?</button>}
          {forgot && <p className="text-center text-[11px] text-[color:var(--text-muted)]">Admin will set a new password and share it with you on WhatsApp/call.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PayDialog({ open, onOpenChange, title, desc, price, note, onRequest, onUnlocked }) {
  const [info, setInfo] = useState(null);
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);
  const name = note;

  useEffect(() => { if (open) getGuessPayInfo().then(setInfo).catch(() => setInfo({})); }, [open]);

  useEffect(() => {
    if (!open || req?.status !== "pending") return undefined;
    const id = setInterval(async () => {
      setLeft(Math.max(0, Math.round((new Date(req.expires_at) - Date.now()) / 1000)));
      try {
        const { items } = await unlockStatus([req.id]);
        const r = items[0];
        if (r && r.status !== "pending") {
          unlockStore.apply(r);
          setReq(r);
          if (r.status === "approved") { toast.success("Payment Success ✅ — guessing unlocked!"); onUnlocked(); }
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(id);
  }, [open, req, onUnlocked]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await onRequest();
      unlockStore.apply(r);
      setReq(r);
      setLeft(Math.max(0, Math.round((new Date(r.expires_at) - Date.now()) / 1000)));
      if (r.status === "approved") { onUnlocked(); onOpenChange(false); }
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  const amt = req?.amount ? Number(req.amount).toFixed(2) : null;
  const upiLink = info?.upi_id && amt
    ? `upi://pay?pa=${encodeURIComponent(info.upi_id)}&pn=${encodeURIComponent(info.upi_name || "BGR MATKA")}&am=${amt}&cu=INR&tn=${encodeURIComponent(name)}`
    : "";
  const status = req?.status;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="unlock-dialog" className={dialogCls}>
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        {!info?.upi_id ? <p className="text-xs text-red-500">Payment details are not set yet.</p>
          : status === "pending" ? (
            <div data-testid="unlock-paying" className="flex flex-col items-center gap-2 rounded-lg bg-[color:var(--bg-secondary)] p-3 text-center">
              <p className="text-sm">Pay the exact amount: <b data-testid="unlock-amount" className="font-mono text-lg text-emerald-600">₹{amt}</b></p>
              <img data-testid="unlock-qr" src={guessPayQrUrl(amt, name)} alt="UPI QR" className="h-44 w-44 rounded bg-white p-1" />
              <a href={upiLink} data-testid="unlock-upi-app" className="btn-gold inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold">
                <Smartphone className="h-4 w-4" /> Pay ₹{amt} via UPI App
              </a>
              <p className="flex items-center gap-1.5 text-xs font-semibold"><Clock className="h-4 w-4 animate-pulse text-amber-500" /> Verifying payment… {left}s</p>
              <p className="text-[10px] text-[color:var(--text-muted)]">Guessing unlocks automatically once payment is received. Pay exactly ₹{amt} (including paise).</p>
            </div>
          ) : status === "approved" ? (
            <div data-testid="unlock-success" className="flex flex-col items-center gap-2 py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <p className="font-bold">Payment Success ✅</p>
            </div>
          ) : (
            <div className="space-y-3">
              {status && (
                <p data-testid="unlock-failed" className="rounded-lg bg-red-500/10 p-2 text-center text-xs font-semibold text-red-600">
                  ❌ Payment not verified (not received within 2 minutes). If money was deducted it will unlock automatically within 5 minutes, otherwise please try again.
                </p>
              )}
              <button type="button" data-testid="unlock-pay-start" onClick={start} disabled={busy}
                className="btn-gold flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-bold disabled:opacity-50">
                <Smartphone className="h-4 w-4" /> {busy ? "..." : `Pay ₹${price}`}
              </button>
              <p className="text-center text-[10px] text-[color:var(--text-muted)]">After tapping Pay, complete the payment within 2 minutes. It is verified automatically.</p>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
